import React from 'react';
import { DataSetReference } from 'libs/end-to-end/src/types/databases/data-sets/data-set-reference';
import { DataSetVersionFull } from 'libs/end-to-end/src/types/databases/data-sets/data-set-version';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { ListItem } from 'libs/end-to-end/src/types/generic/generic';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { DataSet } from 'libs/end-to-end/src/types/databases/data-sets/data-set';

export interface DataSetVersionSelectorModalHook {
  modalTitle: string;
  dataSetItems: ListItem<string>[];
  dataSetVersionItems: ListItem<string>[];
  continueButtonLabel: string;
  continueButtonBehavior: TouchTargetBehavior;
  continueButtonDisabled: boolean;
  isDataSetSelectable: boolean;
  selectedDataSet: string | null;
  setSelectedDataSet: Setter<string | null>;
  selectedDataSetVersion: string | null;
  setSelectedDataSetVersion: Setter<string | null>;
  versionLabel: string;
}

export const useDataSetVersionSelectorModal = ({
  dataSetReference,
  onComplete,
  fullVersions,
}: {
  dataSetReference: DataSetReference | null;
  onComplete: (dataSetReference: DataSetReference) => void;
  organizationId: string;
  fullVersions: DataSetVersionFull[];
}): DataSetVersionSelectorModalHook => {
  const [selectedDataSet, setSelectedDataSet] = useState<string | null>(
    dataSetReference?.dataSetId ?? null,
  );
  
  const isDataSetSelectable = useMemo(() => {
    return dataSetReference === null;
  }, [dataSetReference]);
  
  const [selectedDataSetVersion, setSelectedDataSetVersion] = useState<
    string | null
  >(dataSetReference?.dataSetVersion ?? null);

  // If the selectedDataSetVersion is not in the fullVersions, then it is not valid
  useEffect(() => {
    if (!selectedDataSetVersion) {
      return;
    }

    const fullVersion = fullVersions.find(
      (fullVersion) =>
        fullVersion.dataSetVersion.dataSetVersion === selectedDataSetVersion,
    );

    if (!fullVersion) {
      setSelectedDataSetVersion(null);
      return;
    }
  }, [fullVersions, selectedDataSetVersion]);

  const dataSetItemsToDisplay = useMemo(() => {
    const dataSetsRecord: Record<string, DataSet> = {};
    fullVersions.forEach((version) => {
      dataSetsRecord[version.dataSet.id] = version.dataSet;
    });

    return Object.values(dataSetsRecord).map((dataSet) => {
      return {
        optionLabel: dataSet.displayName,
        optionValue: dataSet.id,
      };
    });
  }, [fullVersions]);

  const dataSetVersionItemsToDisplay = useMemo(() => {
    return fullVersions
      .filter((version) => {
        return version.dataSet.id === selectedDataSet;
      })
      .sort((a, b) => {
        return b.dataSetVersion.createdAt - a.dataSetVersion.createdAt;
      })
      .map((fullVersion) => {
        return {
          optionLabel: `${fullVersion.dataSet.displayName} @ ${new Date(
            fullVersion.dataSetVersion.createdAt,
          )} - ${fullVersion.dataSetVersion.dataSetVersion} (${
            fullVersion.totalNumberOfFiles
          } files)`,
          optionValue: fullVersion.dataSetVersion.dataSetVersion,
        };
      });
  }, [selectedDataSet, fullVersions]);

  return useMemo(() => {
    return {
      modalTitle: 'Select Data Set Version',
      versionLabel: 'Data Set Version',
      continueButtonLabel: 'Continue',
      continueButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          if (selectedDataSetVersion && selectedDataSet) {
            onComplete({
              dataSetId: selectedDataSet,
              dataSetVersion: selectedDataSetVersion,
            });
          }
        },
      },
      dataSetItems: dataSetItemsToDisplay,
      dataSetVersionItems: dataSetVersionItemsToDisplay,
      isDataSetSelectable,
      selectedDataSet,
      setSelectedDataSet,
      selectedDataSetVersion,
      setSelectedDataSetVersion,
      continueButtonDisabled: !selectedDataSetVersion || !selectedDataSet,
    };
  }, [
    dataSetItemsToDisplay,
    dataSetVersionItemsToDisplay,
    onComplete,
    selectedDataSet,
    selectedDataSetVersion,
    isDataSetSelectable,
  ]);
};
